import { Component } from '@theme/component';

/**
 * Mirrors the Quantity Tier Selector's currently selected price next to the
 * product title.
 *
 * It consumes the already-rendered price string from the
 * `quantity-tier:price` event rather than recalculating anything, so the
 * header and the tier card can never show different numbers.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement | undefined} price
 * @property {HTMLElement | undefined} comparePrice
 * @extends {Component<Refs>}
 */
export class ProductTitlePriceComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    document.addEventListener('quantity-tier:price', this.#onTierPrice, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {CustomEvent<{ productId?: string, price?: string, comparePrice?: string }>} event
   */
  #onTierPrice = (event) => {
    const { productId, price, comparePrice } = event.detail ?? {};

    // Ignore selectors wired to a different product on the same page.
    if (!price || productId !== this.dataset.productId) return;

    const priceElement = this.refs.price;
    if (priceElement) priceElement.textContent = price;

    // Only shown for tiers that actually carry a discount.
    const compareElement = this.refs.comparePrice;
    if (compareElement) {
      compareElement.textContent = comparePrice ?? '';
      compareElement.hidden = !comparePrice;
    }
  };
}

if (!customElements.get('product-title-price-component')) {
  customElements.define('product-title-price-component', ProductTitlePriceComponent);
}
